import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SEARCH_CITIES } from "../../../mocks/flights";

const CABIN_CLASSES = ["Economy", "Economy Premium", "Business", "First"];

interface PaxState {
  adults: number;
  children: number;
  infants: number;
}

export default function HeroSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"Flights" | "Insurance">("Flights");
  const [tripType, setTripType] = useState<"One way" | "Return" | "Multi City">("Return");
  const [cabinClass, setCabinClass] = useState("Economy");
  const [showCabinDrop, setShowCabinDrop] = useState(false);
  const [showPaxDrop, setShowPaxDrop] = useState(false);
  const [pax, setPax] = useState<PaxState>({ adults: 1, children: 0, infants: 0 });

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [departure, setDeparture] = useState("");
  const [returning, setReturning] = useState("");
  const [fromSuggestions, setFromSuggestions] = useState<string[]>([]);
  const [toSuggestions, setToSuggestions] = useState<string[]>([]);

  const [from2, setFrom2] = useState("");
  const [to2, setTo2] = useState("");
  const [departure2, setDeparture2] = useState("");
  const [from2Suggestions, setFrom2Suggestions] = useState<string[]>([]);
  const [to2Suggestions, setTo2Suggestions] = useState<string[]>([]);

  const cabinRef = useRef<HTMLDivElement>(null);
  const paxRef = useRef<HTMLDivElement>(null);

  const totalPax = pax.adults + pax.children + pax.infants;
  const paxLabel = `${totalPax} Adult${totalPax > 1 ? "s" : ""}`;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (cabinRef.current && !cabinRef.current.contains(e.target as Node)) setShowCabinDrop(false);
      if (paxRef.current && !paxRef.current.contains(e.target as Node)) setShowPaxDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const updatePax = (key: keyof PaxState, delta: number) => {
    setPax(prev => ({
      ...prev,
      [key]: Math.max(key === "adults" ? 1 : 0, prev[key] + delta),
    }));
  };

  const makeSuggestions = (val: string) =>
    val.length > 0
      ? SEARCH_CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase())).slice(0, 8)
      : [];

  const handleSearch = () => {
    const params = new URLSearchParams({
      from, to, date: departure, tripType, pax: String(totalPax), class: cabinClass,
      ...(tripType === "Return" && returning ? { returnDate: returning } : {}),
      ...(tripType === "Multi City" ? { from2, to2, date2: departure2 } : {}),
    });
    navigate(`/booking-inquiry?${params.toString()}`);
  };

  return (
    <section
      className="relative overflow-hidden pt-16"
      style={{ background: "linear-gradient(150deg, #0c4a6e 0%, #0e7490 22%, #0891b2 45%, #06b6d4 68%, #22d3ee 85%, #67e8f9 100%)" }}
    >
      {/* Amber top accent strip */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#FFE000] z-10" />

      {/* Decorative floating orbs */}
      <div
        className="absolute top-4 right-8 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,158,11,0.25), transparent 70%)" }}
      />
      <div
        className="absolute -bottom-12 -left-12 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(52,211,153,0.18), transparent 70%)" }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-36 h-36 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(251,191,36,0.12), transparent 70%)" }}
      />
      <div className="absolute top-10 left-1/3 w-5 h-5 rounded-full bg-white/20 pointer-events-none" />
      <div className="absolute top-24 right-1/3 w-3 h-3 rounded-full bg-amber-300/40 pointer-events-none" />
      <div className="absolute bottom-20 right-14 w-4 h-4 rounded-full bg-white/15 pointer-events-none" />
      <div className="absolute bottom-10 left-1/4 w-3 h-3 rounded-full bg-emerald-300/30 pointer-events-none" />
      <div className="absolute top-16 left-12 w-2 h-2 rounded-full bg-amber-200/50 pointer-events-none" />

      {/* Hero Text */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-10 pb-5 text-center">
        <p className="inline-flex items-center gap-2 text-amber-300 text-xs font-bold tracking-widest uppercase mb-3">
          <i className="ri-flight-takeoff-line text-sm"></i>
          Pakistan&apos;s Trusted Travel Partner
        </p>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
          Find &amp; Book Your<br />
          <span className="text-amber-300">Next Flight</span>
        </h1>
        <p className="text-white/80 mt-2 text-sm md:text-base font-medium">
          Lowest Fares &middot; Best Deals &middot; Zero Hassle
        </p>
      </div>

      {/* Booking Widget */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-xl overflow-visible shadow-2xl">
          {/* Tabs */}
          <div className="flex rounded-t-xl overflow-hidden">
            {(["Flights", "Insurance"] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-6 md:px-8 py-3 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  activeTab === tab ? "bg-[#FFE000] text-gray-900" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-4 md:p-5">
            {activeTab === "Flights" && (
              <form data-readdy-form onSubmit={e => { e.preventDefault(); handleSearch(); }}>
                {/* Trip Options Row */}
                <div className="flex flex-wrap items-center gap-y-2 gap-x-3 mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {(["One way", "Return", "Multi City"] as const).map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setTripType(type)}
                        className={`text-sm font-medium cursor-pointer pb-0.5 whitespace-nowrap transition-colors ${
                          tripType === type
                            ? "text-[#C9A800] border-b-2 border-[#FFE000]"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 ml-auto">
                    {/* Cabin */}
                    <div className="relative" ref={cabinRef}>
                      <button
                        type="button"
                        onClick={() => { setShowCabinDrop(!showCabinDrop); setShowPaxDrop(false); }}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-700 cursor-pointer hover:text-[#C9A800] transition-colors whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">{cabinClass} Class</span>
                        <span className="sm:hidden">{cabinClass}</span>
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </button>
                      {showCabinDrop && (
                        <div className="absolute top-7 left-0 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50 min-w-[160px]">
                          {CABIN_CLASSES.map(cls => (
                            <button
                              key={cls}
                              type="button"
                              onClick={() => { setCabinClass(cls); setShowCabinDrop(false); }}
                              className={`w-full text-left px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-colors ${
                                cabinClass === cls ? "text-[#C9A800] font-semibold bg-yellow-50" : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {cls}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pax */}
                    <div className="relative" ref={paxRef}>
                      <button
                        type="button"
                        onClick={() => { setShowPaxDrop(!showPaxDrop); setShowCabinDrop(false); }}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-700 cursor-pointer hover:text-[#C9A800] transition-colors whitespace-nowrap"
                      >
                        {paxLabel}
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </button>
                      {showPaxDrop && (
                        <div className="absolute top-7 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50 w-64">
                          <h3 className="text-sm font-bold text-gray-800 mb-3">Travelers</h3>
                          {(
                            [
                              { key: "adults", label: "Adults", sub: "12 years & above" },
                              { key: "children", label: "Child", sub: "2 to under 12 year" },
                              { key: "infants", label: "Infant", sub: "Under 2 years" },
                            ] as { key: keyof PaxState; label: string; sub: string }[]
                          ).map(({ key, label, sub }) => (
                            <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{label}</p>
                                <p className="text-xs text-gray-400">{sub}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <button type="button" onClick={() => updatePax(key, -1)}
                                  className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer text-lg leading-none">–</button>
                                <span className="text-sm font-bold w-4 text-center">{pax[key]}</span>
                                <button type="button" onClick={() => updatePax(key, 1)}
                                  className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer text-lg leading-none">+</button>
                              </div>
                            </div>
                          ))}
                          <button type="button" onClick={() => setShowPaxDrop(false)}
                            className="mt-3 w-full bg-[#FFE000] text-gray-900 text-sm font-semibold py-2 rounded-lg cursor-pointer hover:bg-[#D4AF00] transition-colors whitespace-nowrap">
                            OK
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* One Way / Return fields */}
                {tripType !== "Multi City" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="relative">
                      <p className="text-xs text-gray-500 mb-1">From</p>
                      <input
                        value={from}
                        onChange={e => { setFrom(e.target.value); setFromSuggestions(makeSuggestions(e.target.value)); }}
                        onBlur={() => setTimeout(() => setFromSuggestions([]), 200)}
                        placeholder="Type departure..."
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors"
                      />
                      {fromSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                          {fromSuggestions.map(c => (
                            <button key={c} type="button" onMouseDown={() => { setFrom(c); setFromSuggestions([]); }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <p className="text-xs text-gray-500 mb-1">To</p>
                      <input
                        value={to}
                        onChange={e => { setTo(e.target.value); setToSuggestions(makeSuggestions(e.target.value)); }}
                        onBlur={() => setTimeout(() => setToSuggestions([]), 200)}
                        placeholder="Type arrival..."
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors"
                      />
                      {toSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                          {toSuggestions.map(c => (
                            <button key={c} type="button" onMouseDown={() => { setTo(c); setToSuggestions([]); }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">Departure</p>
                      <div className="flex items-center border border-gray-300 rounded px-3 py-2 gap-2 focus-within:border-[#FFE000] transition-colors">
                        <i className="ri-calendar-line text-gray-400 text-sm"></i>
                        <input type="date" value={departure} onChange={e => setDeparture(e.target.value)}
                          className="flex-1 min-w-0 text-sm text-gray-700 outline-none bg-transparent cursor-pointer" />
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">Return</p>
                      <div className={`flex items-center border rounded px-3 py-2 gap-2 transition-colors ${tripType === "Return" ? "border-gray-300 focus-within:border-[#FFE000]" : "border-gray-200 bg-gray-50"}`}>
                        <i className="ri-calendar-line text-gray-400 text-sm"></i>
                        <input type="date" value={returning} onChange={e => setReturning(e.target.value)}
                          disabled={tripType !== "Return"}
                          className="flex-1 min-w-0 text-sm text-gray-700 outline-none bg-transparent cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Multi City: 2 legs */}
                {tripType === "Multi City" && (
                  <div className="flex flex-col gap-3 mb-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2">
                      <span className="text-xs font-semibold text-gray-900 bg-[#FFE000] rounded px-2 py-1 whitespace-nowrap">Leg 1</span>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 w-full">
                        <div className="relative">
                          <p className="text-xs text-gray-500 mb-1">From</p>
                          <input value={from} onChange={e => { setFrom(e.target.value); setFromSuggestions(makeSuggestions(e.target.value)); }}
                            onBlur={() => setTimeout(() => setFromSuggestions([]), 200)}
                            placeholder="City or airport..."
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors" />
                          {fromSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                              {fromSuggestions.map(c => (
                                <button key={c} type="button" onMouseDown={() => { setFrom(c); setFromSuggestions([]); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <p className="text-xs text-gray-500 mb-1">To</p>
                          <input value={to} onChange={e => { setTo(e.target.value); setToSuggestions(makeSuggestions(e.target.value)); }}
                            onBlur={() => setTimeout(() => setToSuggestions([]), 200)}
                            placeholder="City or airport..."
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors" />
                          {toSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                              {toSuggestions.map(c => (
                                <button key={c} type="button" onMouseDown={() => { setTo(c); setToSuggestions([]); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Departure Date</p>
                          <div className="flex items-center border border-gray-300 rounded px-3 py-2 gap-2 focus-within:border-[#FFE000] transition-colors">
                            <i className="ri-calendar-line text-gray-400 text-sm"></i>
                            <input type="date" value={departure} onChange={e => setDeparture(e.target.value)}
                              className="flex-1 min-w-0 text-sm text-gray-700 outline-none bg-transparent cursor-pointer" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2">
                      <span className="text-xs font-semibold text-amber-600 bg-amber-50 rounded px-2 py-1 whitespace-nowrap">Leg 2</span>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 w-full">
                        <div className="relative">
                          <p className="text-xs text-gray-500 mb-1">From</p>
                          <input value={from2} onChange={e => { setFrom2(e.target.value); setFrom2Suggestions(makeSuggestions(e.target.value)); }}
                            onBlur={() => setTimeout(() => setFrom2Suggestions([]), 200)}
                            placeholder="City or airport..."
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors" />
                          {from2Suggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                              {from2Suggestions.map(c => (
                                <button key={c} type="button" onMouseDown={() => { setFrom2(c); setFrom2Suggestions([]); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <p className="text-xs text-gray-500 mb-1">To</p>
                          <input value={to2} onChange={e => { setTo2(e.target.value); setTo2Suggestions(makeSuggestions(e.target.value)); }}
                            onBlur={() => setTimeout(() => setTo2Suggestions([]), 200)}
                            placeholder="City or airport..."
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#FFE000] transition-colors" />
                          {to2Suggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto mt-1">
                              {to2Suggestions.map(c => (
                                <button key={c} type="button" onMouseDown={() => { setTo2(c); setTo2Suggestions([]); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 cursor-pointer whitespace-nowrap">{c}</button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Departure Date</p>
                          <div className="flex items-center border border-gray-300 rounded px-3 py-2 gap-2 focus-within:border-[#FFE000] transition-colors">
                            <i className="ri-calendar-line text-gray-400 text-sm"></i>
                            <input type="date" value={departure2} onChange={e => setDeparture2(e.target.value)}
                              className="flex-1 min-w-0 text-sm text-gray-700 outline-none bg-transparent cursor-pointer" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Book Button */}
                <div className="flex justify-center mt-2">
                  <button type="submit"
                    className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-8 md:px-10 py-2.5 rounded text-sm font-bold cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-flight-takeoff-line text-base"></i>
                    Book Your Flight
                  </button>
                </div>
              </form>
            )}

            {activeTab === "Insurance" && (
              <div className="py-8 text-center">
                <i className="ri-shield-check-line text-4xl text-[#C9A800] mb-3 block"></i>
                <p className="text-gray-700 font-semibold">Travel Insurance Plans</p>
                <p className="text-gray-500 text-sm mt-1">Speak with our team on 03711432451 to get a personalised insurance quote.</p>
                <a href="/contact"
                  className="inline-block mt-4 bg-[#FFE000] text-gray-900 px-6 py-2 rounded text-sm font-semibold cursor-pointer hover:bg-[#D4AF00] transition-colors whitespace-nowrap">
                  Request a Quote
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Smooth wave into white */}
      <div className="relative h-10 overflow-hidden">
        <div
          className="absolute inset-0 bg-white"
          style={{ clipPath: "ellipse(55% 100% at 50% 100%)" }}
        />
      </div>
    </section>
  );
}
